import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  fetchFiles,
  clearFilesList,
  setFilesParams,
  setFilesFilters,
  clearFilesFiltersAndReload,
  clearSelectedFilters,
} from '../../actions/Files';

import FiltersSearch from './FiltersSearch';
import FiltersFileStatuses from './FiltersFileStatuses';
import FiltersFileTypes from './FiltersFileTypes';
import FiltersFileExtensions from './FiltersFileExtensions';
import FiltersFileTerms from './FiltersFileTerms';
import FiltersFileDates from './FiltersFileDates';
import FiltersFileVisibility from './FiltersFileVisibility';
import FiltersFileUsers from './FiltersFileUsers';
import FiltersFilePeople from './FiltersFilePeople';
import FiltersFileOrientation from './FiltersFileOrientation';
import FiltersFileNoCollections from './FiltersFileNoCollections';
import FiltersFileDeletedBy from './FiltersFileDeletedBy';
import FiltersFileMetadataGroup from './FiltersFileMetadataGroup';
import FiltersPinnedSearches from './FiltersPinnedSearches';

import { userIsAdmin } from '../../helpers/Auth';
import {
  setSearchFields,
  setSearchTerm,
} from '../../actions/Search';
import { getSetting } from '../../helpers/Settings';
import {
  filterParams,
  handleComponentParams,
  handleURLParams,
  handleURLParamsClear,
} from '../../helpers/URLParams';

class SidebarFilters extends PureComponent {
  constructor(props) {
    super(props);

    const savedFilters = getSetting('filters_files') || [];
    const savedGroups = {};
    savedFilters.forEach((f) => {
      if (/^tax_group_\d+$/.test(f.key)) {
        savedGroups[f.key] = f;
      }
    });

    this.state = {
      openFilters: (props.metadataGroups || [])
        .map(g => `tax_group_${g.id}`)
        .filter((key) => {
          const saved = savedGroups[key];
          return !saved || saved.open_by_default;
        }),
    };

    this.handleChangeFilters = this.handleChangeFilters.bind(this);
    this.handleToggleFilter = this.handleToggleFilter.bind(this);
    this.resetFiltersWrapper = this.resetFiltersWrapper.bind(this);
  }

  componentDidMount() {
    const {
      updateFilters,
      location,
      selectedFilters,
      externalSearch,
    } = this.props;
    if (externalSearch) return;

    const searchParams = new URLSearchParams(location.search);

    if (searchParams.toString() !== '' && selectedFilters.length === 0) {
      handleComponentParams(
        true,
        filterParams,
        null,
        this.setState.bind(this),
        (updatedFilters) => {
          updateFilters(updatedFilters);
        },
      );
    }
  }

  componentDidUpdate(prevProps) {
    const {
      updateFilters,
      resetSelectedFilters,
      location,
      externalSearch,
    } = this.props;
    if (externalSearch) return;

    if (
      !(location.state && location.state.preventUpdateParams) &&
      (
        location.pathname === '/results' ||
        location.pathname === '/files' ||
        location.pathname === '/collections'
      )
    ) {
      handleComponentParams(
        false,
        filterParams,
        prevProps,
        this.setState.bind(this),
        (updatedFilters) => {
          resetSelectedFilters();
          updateFilters(updatedFilters);
        },
      );
    }
  }

  handleChangeFilters(changedFilters, reset = false) {
    const {
      updateFilters,
      resetSelectedFilters,
      history,
      searchPathSuffix,
      externalSearch,
    } = this.props;
    if (reset) {
      resetSelectedFilters();
    }
    handleURLParams(changedFilters, history, {}, searchPathSuffix);
    if (!externalSearch) {
      updateFilters(changedFilters);
    }
  }

  handleToggleFilter(filter) {
    const { openFilters } = this.state;

    if (openFilters.includes(filter)) {
      this.setState({ openFilters: openFilters.filter(cf => (cf !== filter)) });
    } else {
      this.setState({ openFilters: openFilters.concat([filter]) });
    }
  }

  resetFiltersWrapper() {
    const {
      resetFilters,
      history,
      externalSearch,
    } = this.props;
    handleURLParamsClear([...filterParams, 'search', 'search_term', 'search_fields'], history);
    if (!externalSearch) {
      resetFilters();
    }
  }

  render() {
    const { openFilters } = this.state;
    const {
      intl,
      type,
      filesLoading,
      availableFilters,
      selectedFilters,
      taxonomies,
      metadataGroups,
      collection,
      hidden,
      externalSearch,
      searchPathSuffix,
      inPopover,
      location,
      history,
    } = this.props;
    const urlSearch = externalSearch || !!searchPathSuffix;
    const filters = getSetting('filters_files');
    const taxonomyFilters = [];
    const metadataGroupFilters = [];

    return (
      <div className={classnames({ hidden })}>
        <div className="filters-header">
          <h5 className="filters-title">
            <i className="far fa-sliders-h" />
            {intl.formatMessage({ defaultMessage: 'Filters' })}
          </h5>
          <Button
            color="link"
            className={classnames('action-clear', 'action-clear-all', {
              invisible: selectedFilters.length === 0 || filesLoading,
            })}
            onClick={this.resetFiltersWrapper}
          >
            {intl.formatMessage({ defaultMessage: 'Clear all' })}
          </Button>
        </div>
        <div className="files-filters">
          <FiltersSearch
            type={type}
            allowDisabled={!urlSearch}
            customSearch={urlSearch ? new URLSearchParams(location.search).get('search') || '' : undefined}
            customSetSearch={urlSearch
              ? search => handleURLParams({ search }, history, {}, searchPathSuffix) : undefined}
            customResetSearch={urlSearch
              ? () => handleURLParams(
                {
                  search: null,
                  search_fields: [],
                },
                history,
                {},
                searchPathSuffix,
              ) :
              undefined}
          />
          <FiltersPinnedSearches
            handleReset={this.resetFiltersWrapper}
            handleChangeFilters={this.handleChangeFilters}
            selectedFilters={selectedFilters}
            type="files"
          />
          {filters.map((filter) => {
            if (!filter.visible || (filter.admins_only && !userIsAdmin())) {
              if (filter.key && filter.key.indexOf('tax_') === 0) {
                taxonomyFilters.push(parseInt(filter.key.replace('tax_', '')));
              }
              return false;
            }

            if (filter.key === 'f_deleted_by') {
              return (
                <FiltersFileDeletedBy
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_status') {
              return (
                <FiltersFileStatuses
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.statuses}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_type') {
              return (
                <FiltersFileTypes
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.types}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_extension') {
              return (
                <FiltersFileExtensions
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.extensions}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_visibility') {
              return (
                <FiltersFileVisibility
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.visibility}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_no_collections' && collection === null) {
              return (
                <FiltersFileNoCollections
                  key={filter.key}
                  handleChangeFilters={this.handleChangeFilters}
                />
              );
            }

            if (filter.key === 'f_owner') {
              return (
                <FiltersFileUsers
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_people' && getSetting('face_recognition')) {
              return (
                <FiltersFilePeople
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_original_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  dateMode="original"
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_expiration_date') {
              return (
                <FiltersFileDates
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  dateMode="expiration"
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key === 'f_orientation') {
              return (
                <FiltersFileOrientation
                  key={filter.key}
                  isOpen={openFilters.includes(filter.key)}
                  availableFilters={availableFilters.orientation}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(filter.key); }}
                />
              );
            }

            if (filter.key.indexOf('tax_group') === 0) {
              const metadataGroupId = parseInt(filter.key.replace('tax_group_', ''));
              const metadataGroup = metadataGroups.find(tg => (tg.id === metadataGroupId));
              if (!metadataGroup) {
                return null;
              }
              metadataGroupFilters.push(metadataGroup.id);

              return (
                <FiltersFileMetadataGroup
                  key={`tax_group_${metadataGroup.id}`}
                  isOpen={openFilters.includes(`tax_group_${metadataGroup.id}`)}
                  metadataGroup={metadataGroup}
                  openFilters={openFilters}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={this.handleToggleFilter}
                  inPopover={inPopover}
                />
              );
            }

            if (filter.key.indexOf('tax_') === 0) {
              const taxonomyId = parseInt(filter.key.replace('tax_', ''));
              const taxonomy = taxonomies.find(t => (t.id === taxonomyId));
              if (!taxonomy) {
                return null;
              }
              taxonomyFilters.push(taxonomy.id);

              if ((taxonomy.ai_tagging && !getSetting('ai_tagging')) || taxonomy.metadata_groups?.length > 0) {
                return null;
              }

              return (
                <FiltersFileTerms
                  key={`tax_${taxonomy.id}`}
                  isOpen={openFilters.includes(`tax_${taxonomy.id}`)}
                  taxonomy={taxonomy}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(`tax_${taxonomy.id}`); }}
                  inPopover={inPopover}
                />
              );
            }

            return null;
          })}

          {taxonomies.map((tax) => {
            if (!taxonomyFilters.includes(tax.id)) {
              return (
                <FiltersFileTerms
                  key={`tax_${tax.id}`}
                  isOpen={openFilters.includes(`tax_${tax.id}`)}
                  taxonomy={tax}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={() => { this.handleToggleFilter(`tax_${tax.id}`); }}
                  inPopover={inPopover}
                />
              );
            }
            return null;
          })}

          {metadataGroups.map((taxGroup) => {
            if (!metadataGroupFilters.includes(taxGroup.id)) {
              return (
                <FiltersFileMetadataGroup
                  key={`tax_group_${taxGroup.id}`}
                  isOpen={openFilters.includes(`tax_group_${taxGroup.id}`)}
                  metadataGroup={taxGroup}
                  openFilters={openFilters}
                  handleChangeFilters={this.handleChangeFilters}
                  handleToggleFilter={this.handleToggleFilter}
                  inPopover={inPopover}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  filesLoading: state.Files.loading,
  availableFilters: state.Files.availableFilters,
  selectedFilters: state.Files.selectedFilters,
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
  collection: state.Collections.collection,
});

const mapDispatchToProps = dispatch => ({
  updateFilters: (updatedFilters) => {
    dispatch(setFilesFilters(updatedFilters));
    dispatch(clearFilesList());
    dispatch(setFilesParams({ page: 0 }));
    dispatch(fetchFiles());
  },
  resetFilters: () => {
    dispatch(setSearchTerm(''));
    dispatch(setSearchFields([]));
    dispatch(clearFilesFiltersAndReload());
  },
  resetSelectedFilters: () => {
    dispatch(clearSelectedFilters());
  },
});

SidebarFilters.propTypes = {
  intl: PropTypes.object.isRequired,
  type: PropTypes.string,
  filesLoading: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  availableFilters: PropTypes.object.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  resetSelectedFilters: PropTypes.func.isRequired,
  collection: PropTypes.object,
  hidden: PropTypes.bool,
  externalSearch: PropTypes.bool,
  searchPathSuffix: PropTypes.string,
  inPopover: PropTypes.bool,
};

SidebarFilters.defaultProps = {
  type: null,
  collection: null,
  hidden: false,
  searchPathSuffix: null,
  inPopover: false,
};

export default withRouter(
  injectIntl(connect(mapStateToProps, mapDispatchToProps)(SidebarFilters)),
);
